/**
 * Simulated trading data, so the panel can be explored before there is a
 * database behind it.
 *
 * Everything here is generated from the REAL 91-product catalogue — real names,
 * real prices, real categories — and then given a plausible six months of
 * trading. That makes the demo useful rather than decorative: the charts show
 * Lorenzini shirts outselling socks because shirts cost more and sell more, not
 * because of a random number.
 *
 * ── Why it is seeded ──────────────────────────────────────────────────────────
 * A `Math.random()` dashboard changes every time it is opened, which makes it
 * impossible to point at a number and discuss it, and makes any visual
 * regression meaningless. A fixed-seed PRNG gives the same figures on every
 * load and on every machine.
 *
 * ── The honesty rule ─────────────────────────────────────────────────────────
 * Demo mode is never silent. `isDemo` is surfaced in the UI as a permanent
 * banner, because a fake revenue figure mistaken for a real one is worse than no
 * figure at all. The moment real Supabase credentials are present, none of this
 * code runs.
 */

import catalogue from '@catalog/products.json'

export const DEMO_NOTICE =
  'Demo data — simulated orders and statistics. Connect Supabase to see real figures.'

// ---------------------------------------------------------------------------
//  Deterministic PRNG (mulberry32)
// ---------------------------------------------------------------------------
//  Small, fast, and good enough for plausible-looking business data. Not for
//  anything security-related.
// ---------------------------------------------------------------------------
function mulberry32(seed) {
  let a = seed >>> 0
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const rand = mulberry32(20260910)
const pick = (arr) => arr[Math.floor(rand() * arr.length)]
const between = (min, max) => min + rand() * (max - min)
const intBetween = (min, max) => Math.floor(between(min, max + 1))

// ---------------------------------------------------------------------------
//  Shape of the simulation
// ---------------------------------------------------------------------------
const DAYS = 180
const TODAY = new Date()
TODAY.setHours(12, 0, 0, 0)

const FIRST_NAMES = [
  'Johan', 'Thabo', 'Pieter', 'Sipho', 'Andre', 'Kagiso', 'Riaan', 'Lebo',
  'Hendrik', 'Tshepo', 'Willem', 'Mandla', 'Francois', 'Bongani', 'Dirk',
  'Ntando', 'Gerhard', 'Themba', 'Marius', 'Sibusiso',
]
const LAST_NAMES = [
  'van der Merwe', 'Nkosi', 'Botha', 'Dlamini', 'Pretorius', 'Mokoena',
  'Venter', 'Khumalo', 'Steyn', 'Mahlangu', 'Fourie', 'Zulu', 'Coetzee',
  'Ndlovu', 'Joubert', 'Sithole', 'Kruger', 'Mabaso',
]
// Centurion and the surrounding Pretoria suburbs — where the shop actually is.
const CITIES = [
  ['Centurion', 'Gauteng', '0157'],
  ['Pretoria', 'Gauteng', '0002'],
  ['Midrand', 'Gauteng', '1685'],
  ['Johannesburg', 'Gauteng', '2000'],
  ['Bronkhorstspruit', 'Gauteng', '1020'],
  ['Polokwane', 'Limpopo', '0699'],
  ['Bloemfontein', 'Free State', '9301'],
  ['Nelspruit', 'Mpumalanga', '1200'],
]
const STREETS = [
  'Galway Avenue', 'Jean Avenue', 'Lenchen Avenue', 'Hendrik Verwoerd Drive',
  'Botha Avenue', 'John Vorster Drive', 'Rabie Street', 'Church Street',
]

/**
 * A weight per product, so some lines are genuinely popular and others barely
 * move — which is the whole point of a "what is selling" report.
 *
 * Weighting is deliberately not uniform random: dearer shirts and the Polo range
 * are pushed up because that is how a menswear shop actually trades, and socks
 * and vests are high-volume/low-value. A flat distribution would make every
 * chart look the same and hide the shape the owner needs to see.
 */
const POPULARITY = new Map(
  catalogue.map((p) => {
    let weight = between(0.2, 1)
    if (p.category === 'Polo') weight *= 2.4
    if (p.category === 'Long Sleeve Shirts') weight *= 1.9
    if (p.category === 'Short Sleeve Shirts') weight *= 1.5
    if (p.category === 'Underwear') weight *= 2.1 // cheap, bought in multiples
    if (p.category === 'Socks') weight *= 1.8
    if (p.category === 'Shoes') weight *= 0.55 // dear, considered purchase
    if (p.category === 'Jackets') weight *= 0.8
    if (p.brand === 'Lorenzini') weight *= 1.35
    return [p.id, weight]
  }),
)

const WEIGHT_TOTAL = [...POPULARITY.values()].reduce((a, b) => a + b, 0)

function weightedProduct() {
  let roll = rand() * WEIGHT_TOTAL
  for (const p of catalogue) {
    roll -= POPULARITY.get(p.id)
    if (roll <= 0) return p
  }
  return catalogue[0]
}

/**
 * Orders per day.
 *
 * Shaped rather than flat, because a flat line makes the time-series chart
 * pointless. Three effects are layered:
 *
 *   - a gentle upward trend across the six months (the business growing);
 *   - a weekly rhythm — Saturday is the busiest day for a Centurion menswear
 *     shop and Sunday is closed, which the shop's own opening hours confirm;
 *   - a payday bump around the 25th, which is how South African retail behaves.
 */
function ordersForDay(dayIndex, date) {
  const dow = date.getDay()
  const dom = date.getDate()

  const trend = 1 + (dayIndex / DAYS) * 0.55
  const weekly = [0.15, 0.85, 0.9, 1.0, 1.1, 1.45, 1.7][dow] // Sun…Sat
  const payday = dom >= 24 && dom <= 28 ? 1.5 : dom <= 3 ? 1.2 : 1
  const noise = between(0.65, 1.35)

  const expected = 2.1 * trend * weekly * payday * noise
  // Floor at zero, and allow genuinely quiet days — a dashboard that never
  // shows a zero teaches the owner nothing about variance.
  return Math.max(0, Math.round(expected))
}

const STATUS_BY_AGE = (daysAgo) => {
  // Recent orders are still moving through the pipeline; old ones have landed.
  if (daysAgo <= 1) return pick(['pending', 'pending', 'paid'])
  if (daysAgo <= 3) return pick(['paid', 'processing', 'processing'])
  if (daysAgo <= 6) return pick(['processing', 'shipped', 'shipped'])
  if (daysAgo <= 10) return pick(['shipped', 'delivered', 'delivered'])
  return pick([
    'delivered', 'delivered', 'delivered', 'delivered', 'delivered',
    'delivered', 'delivered', 'delivered', 'cancelled', 'refunded',
  ])
}

// ---------------------------------------------------------------------------
//  Build the order book once, at module load
// ---------------------------------------------------------------------------
function buildOrders() {
  const orders = []
  let sequence = 1001

  for (let d = 0; d < DAYS; d += 1) {
    const date = new Date(TODAY)
    date.setDate(date.getDate() - (DAYS - 1 - d))
    const daysAgo = DAYS - 1 - d
    const count = ordersForDay(d, date)

    for (let n = 0; n < count; n += 1) {
      const placedAt = new Date(date)
      // Trading hours: 9am–5pm weekdays, 9am–2pm Saturday.
      placedAt.setHours(intBetween(9, date.getDay() === 6 ? 14 : 17), intBetween(0, 59), 0, 0)

      const itemCount = rand() < 0.55 ? 1 : rand() < 0.85 ? 2 : intBetween(3, 5)
      const items = []
      const seen = new Set()

      for (let i = 0; i < itemCount; i += 1) {
        const product = weightedProduct()
        // Same garment twice in one order is usually a different size, but
        // keeping lines distinct reads more clearly in the UI.
        if (seen.has(product.id)) continue
        seen.add(product.id)

        const quantity = rand() < 0.8 ? 1 : intBetween(2, 3)
        items.push({
          id: `demo-item-${placedAt.getTime()}-${i}`,
          product_id: `demo-${product.id}`,
          public_product_id: product.id,
          sku: product.sku,
          name: product.name,
          image: product.image,
          size: product.sizes?.length ? pick(product.sizes) : null,
          color: product.colors?.length ? pick(product.colors) : null,
          sub_style: null,
          unit_price: product.price,
          quantity,
          line_total: product.price * quantity,
        })
      }

      if (items.length === 0) continue

      const subtotal = items.reduce((sum, i) => sum + i.line_total, 0)
      // Mirrors the real rule in store_settings: R199, free over R1500.
      const shipping = subtotal >= 1500 ? 0 : 199
      const status = STATUS_BY_AGE(daysAgo)
      const firstName = pick(FIRST_NAMES)
      const lastName = pick(LAST_NAMES)
      const [city, province, postal] = pick(CITIES)

      orders.push({
        id: `demo-order-${sequence}`,
        order_number: `COQ-${placedAt.toISOString().slice(2, 10).replace(/-/g, '')}-${String(sequence).padStart(4, '0')}`,
        status,
        // A pending order has not been paid for; everything else has, apart
        // from a refund which is recorded as refunded.
        payment_state:
          status === 'pending' ? 'pending' : status === 'refunded' ? 'refunded' : 'complete',
        payment_method: 'payfast',
        payment_reference: status === 'pending' ? null : `pf_${intBetween(1000000, 9999999)}`,
        paid_at: status === 'pending' ? null : placedAt.toISOString(),
        customer_first_name: firstName,
        customer_last_name: lastName,
        customer_email: `${firstName.toLowerCase().replace(/[^a-z]/g, '')}.${lastName.toLowerCase().replace(/[^a-z]/g, '')}@example.co.za`,
        customer_phone: `0${intBetween(60, 84)} ${intBetween(100, 999)} ${intBetween(1000, 9999)}`,
        shipping_address: `${intBetween(1, 480)} ${pick(STREETS)}`,
        shipping_city: city,
        shipping_province: province,
        shipping_postal_code: postal,
        subtotal,
        shipping_amount: shipping,
        discount_amount: 0,
        total: subtotal + shipping,
        item_count: items.reduce((sum, i) => sum + i.quantity, 0),
        currency: 'ZAR',
        customer_note: rand() < 0.08 ? 'Please phone before delivery.' : null,
        admin_notes: null,
        tracking_number:
          status === 'shipped' || status === 'delivered' ? `CGX${intBetween(10000000, 99999999)}` : null,
        placed_at: placedAt.toISOString(),
        items,
      })
      sequence += 1
    }
  }

  // Newest first, the order every screen wants them in.
  return orders.reverse()
}

const ORDERS = buildOrders()

/** Paid, non-cancelled — the only orders that count as revenue. */
const isRealised = (o) => o.payment_state === 'complete' && o.status !== 'cancelled'

// ---------------------------------------------------------------------------
//  Inventory
// ---------------------------------------------------------------------------
//  One line per size (and colour where the garment has them), with a
//  deliberately uneven spread so the low-stock panel has something real to say.
// ---------------------------------------------------------------------------
function buildInventory() {
  const rows = []
  for (const p of catalogue) {
    const sizes = p.sizes?.length ? p.sizes : ['']
    const colors = p.colors?.length ? p.colors.slice(0, 4) : ['']
    for (const size of sizes) {
      for (const color of colors) {
        // Middle sizes sell through fastest, which is why M and L are the ones
        // that run out. Modelling that makes the restock list believable.
        const hot = size === 'M' || size === 'L' || size === '32'
        const roll = rand()
        let quantity
        if (roll < 0.08) quantity = 0
        else if (roll < 0.22) quantity = intBetween(1, 3)
        else if (hot) quantity = intBetween(2, 9)
        else quantity = intBetween(4, 22)

        rows.push({
          id: `demo-inv-${p.id}-${size}-${color}`,
          product_id: `demo-${p.id}`,
          public_id: p.id,
          name: p.name,
          sku: p.sku,
          image: p.image,
          category: p.category,
          size,
          color,
          quantity,
          reserved: 0,
          low_stock_threshold: null,
          available: quantity,
        })
      }
    }
  }
  return rows
}

const INVENTORY = buildInventory()

// ---------------------------------------------------------------------------
//  Product view counts
// ---------------------------------------------------------------------------
//  Correlated with popularity but not identical — the gap between the two is the
//  interesting signal. A few products are deliberately given heavy traffic and
//  poor conversion, which is exactly the case the owner wants flagged.
// ---------------------------------------------------------------------------
const VIEWS = new Map(
  catalogue.map((p, i) => {
    const weight = POPULARITY.get(p.id)
    const base = weight * between(40, 90)
    // Every 13th product is a "lots of looks, no sales" case.
    const browseOnly = i % 13 === 0 ? between(2.5, 4) : 1
    return [p.id, Math.round(base * browseOnly)]
  }),
)

// ---------------------------------------------------------------------------
//  Derived series
// ---------------------------------------------------------------------------

function dayKey(iso) {
  return iso.slice(0, 10)
}

export function demoDailySales({ days = 30 } = {}) {
  const byDay = new Map()
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(TODAY)
    d.setDate(d.getDate() - i)
    byDay.set(d.toISOString().slice(0, 10), {
      day: d.toISOString().slice(0, 10),
      revenue: 0,
      order_count: 0,
      units: 0,
    })
  }
  for (const o of ORDERS) {
    if (!isRealised(o)) continue
    const key = dayKey(o.placed_at)
    const row = byDay.get(key)
    if (!row) continue
    row.revenue += o.total
    row.order_count += 1
    row.units += o.item_count
  }
  return [...byDay.values()]
}

export function demoProductPerformance() {
  const sales = new Map()
  for (const o of ORDERS) {
    if (!isRealised(o)) continue
    for (const item of o.items) {
      const row = sales.get(item.public_product_id) ?? { units: 0, revenue: 0, orders: 0, last: null }
      row.units += item.quantity
      row.revenue += item.line_total
      row.orders += 1
      if (!row.last || o.placed_at > row.last) row.last = o.placed_at
      sales.set(item.public_product_id, row)
    }
  }

  return catalogue.map((p) => {
    const s = sales.get(p.id) ?? { units: 0, revenue: 0, orders: 0, last: null }
    const views = VIEWS.get(p.id) ?? 0
    const stock = INVENTORY.filter((i) => i.public_id === p.id).reduce((n, i) => n + i.available, 0)
    return {
      id: `demo-${p.id}`,
      public_id: p.id,
      name: p.name,
      sku: p.sku,
      brand: p.brand,
      price: p.price,
      image: p.image,
      status: 'active',
      in_stock: stock > 0,
      category: p.category,
      units_sold: s.units,
      revenue: s.revenue,
      order_count: s.orders,
      last_sold_at: s.last,
      views,
      add_to_carts: Math.round(s.units * between(1.6, 2.8)),
      conversion_rate: views > 0 ? Number(((s.units / views) * 100).toFixed(1)) : null,
      stock_on_hand: stock,
      gross_profit: null, // no cost prices in the static catalogue
    }
  })
}

export function demoTrending() {
  const window = (fromDays, toDays) => {
    const from = new Date(TODAY); from.setDate(from.getDate() - fromDays)
    const to = new Date(TODAY); to.setDate(to.getDate() - toDays)
    const units = new Map()
    for (const o of ORDERS) {
      if (!isRealised(o)) continue
      const at = new Date(o.placed_at)
      if (at < from || at >= to) continue
      for (const item of o.items) {
        units.set(item.public_product_id, (units.get(item.public_product_id) ?? 0) + item.quantity)
      }
    }
    return units
  }

  const recent = window(7, 0)
  const prior = window(14, 7)

  return catalogue
    .map((p) => {
      const now = recent.get(p.id) ?? 0
      const was = prior.get(p.id) ?? 0
      return {
        id: `demo-${p.id}`,
        public_id: p.id,
        name: p.name,
        image: p.image,
        price: p.price,
        category: p.category,
        units_7d: now,
        units_prev_7d: was,
        views_7d: Math.round((VIEWS.get(p.id) ?? 0) * 0.12),
        units_change_pct: was > 0 ? Number((((now - was) / was) * 100).toFixed(1)) : null,
      }
    })
    .filter((r) => r.units_7d > 0 || r.units_prev_7d > 0)
}

export function demoCategoryPerformance() {
  const perf = demoProductPerformance()
  const byCategory = new Map()
  for (const row of perf) {
    const key = row.category || 'Uncategorised'
    const agg = byCategory.get(key) ?? {
      name: key, product_count: 0, in_stock_count: 0, units_sold: 0, revenue: 0, views: 0,
    }
    agg.product_count += 1
    if (row.in_stock) agg.in_stock_count += 1
    agg.units_sold += row.units_sold
    agg.revenue += row.revenue
    agg.views += row.views
    byCategory.set(key, agg)
  }
  return [...byCategory.values()].sort((a, b) => b.revenue - a.revenue)
}

export function demoDashboardSummary() {
  const monthStart = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1)
  const lastMonthStart = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1)
  const weekStart = new Date(TODAY); weekStart.setDate(weekStart.getDate() - 7)

  const sum = (filter) =>
    ORDERS.filter((o) => isRealised(o) && filter(new Date(o.placed_at))).reduce((n, o) => n + o.total, 0)

  const lowStock = demoLowStock()

  return {
    active_products: catalogue.length,
    draft_products: 0,
    out_of_stock_products: demoProductPerformance().filter((p) => !p.in_stock).length,
    low_stock_lines: lowStock.length,
    pending_orders: ORDERS.filter((o) => o.status === 'pending').length,
    orders_to_fulfil: ORDERS.filter((o) => o.status === 'paid' || o.status === 'processing').length,
    revenue_this_month: sum((d) => d >= monthStart),
    revenue_last_month: sum((d) => d >= lastMonthStart && d < monthStart),
    revenue_7d: sum((d) => d >= weekStart),
    orders_7d: ORDERS.filter((o) => isRealised(o) && new Date(o.placed_at) >= weekStart).length,
    views_7d: [...VIEWS.values()].reduce((a, b) => a + b, 0),
    sessions_7d: Math.round([...VIEWS.values()].reduce((a, b) => a + b, 0) * 0.42),
    total_units_on_hand: INVENTORY.reduce((n, i) => n + i.quantity, 0),
    stock_value_at_cost: 0, // unknown without cost prices
  }
}

export function demoLowStock() {
  return INVENTORY.filter((i) => i.available <= 3).sort((a, b) => a.available - b.available)
}

export function demoOrders({ status, search, limit = 200 } = {}) {
  let rows = ORDERS
  if (status && status !== 'all') rows = rows.filter((o) => o.status === status)
  if (search) {
    const q = search.toLowerCase()
    rows = rows.filter(
      (o) =>
        o.order_number.toLowerCase().includes(q) ||
        `${o.customer_first_name} ${o.customer_last_name}`.toLowerCase().includes(q) ||
        (o.customer_email ?? '').toLowerCase().includes(q),
    )
  }
  return rows.slice(0, limit)
}

export function demoOrder(id) {
  return ORDERS.find((o) => o.id === id || o.order_number === id) ?? null
}

export function demoStatusCounts() {
  const counts = new Map()
  for (const o of ORDERS) counts.set(o.status, (counts.get(o.status) ?? 0) + 1)
  return counts
}

export function demoProducts() {
  const perf = new Map(demoProductPerformance().map((r) => [r.public_id, r]))
  return catalogue.map((p, index) => ({
    id: `demo-${p.id}`,
    public_id: p.id,
    sku: p.sku,
    slug: String(p.name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    name: p.name,
    description: p.description,
    brand: p.brand,
    price: p.price,
    compare_at_price: null,
    cost_price: null,
    category: p.category,
    image: p.image,
    images: p.images ?? [],
    sizes: p.sizes ?? [],
    colors: p.colors ?? [],
    color_swatches: p.colorSwatches ?? {},
    color_images: p.colorImages ?? {},
    variants: p.variants ?? [],
    status: 'active',
    featured: Boolean(p.featured),
    track_inventory: true,
    in_stock: perf.get(p.id)?.in_stock ?? true,
    sort_order: index * 10,
    units_sold: perf.get(p.id)?.units_sold ?? 0,
    revenue: perf.get(p.id)?.revenue ?? 0,
    views: perf.get(p.id)?.views ?? 0,
    stock_on_hand: perf.get(p.id)?.stock_on_hand ?? 0,
    updated_at: new Date(TODAY).toISOString(),
  }))
}

export function demoInventory() {
  return INVENTORY
}

export function demoCategories() {
  const counts = new Map()
  for (const p of catalogue) counts.set(p.category, (counts.get(p.category) ?? 0) + 1)
  return [
    'Polo', 'Short Sleeve Shirts', 'Long Sleeve Shirts', 'Golf Shirts', 'Jackets',
    'Chinos', 'Denim', 'Knitwear', 'Underwear', 'Socks', 'Shoes', 'Pringle',
  ].map((name, i) => ({
    id: `demo-cat-${i}`,
    name,
    slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    icon: 'FaTshirt',
    sort_order: i * 10,
    is_active: true,
    product_count: counts.get(name) ?? 0,
  }))
}

export function demoActivity() {
  const actions = [
    ['product.updated', 'Changed the price of Polo Pique Golfer to R895.00'],
    ['inventory.adjusted', 'Avery Lounge Shirt · L — restocked 12 units'],
    ['order.status_changed', 'COQ-260908-1184: processing → shipped'],
    ['product.created', 'Added Pringle Lambswool V-Neck'],
    ['inventory.adjusted', 'Carter Golfer · Navy · M — stocktake set to 4'],
    ['order.status_changed', 'COQ-260907-1179: paid → processing'],
    ['settings.updated', 'Free shipping threshold changed to R1 500.00'],
    ['product.updated', 'Florsheim Berkley marked out of stock'],
  ]
  return actions.map(([action, summary], i) => {
    const at = new Date(TODAY)
    at.setHours(at.getHours() - i * 7 - 2)
    return {
      id: i + 1,
      action,
      summary,
      actor_email: i % 3 === 0 ? 'owner@coquis.co.za' : 'shop@coquis.co.za',
      created_at: at.toISOString(),
    }
  })
}

/**
 * Example team, so the permissions screen is legible in demo mode.
 * Addresses are example.co.za — never a real inbox.
 */
export function demoTeam() {
  const ago = (hours) => {
    const d = new Date(TODAY)
    d.setHours(d.getHours() - hours)
    return d.toISOString()
  }
  return [
    { id: 'demo-user', email: 'demo@coquis.co.za', full_name: 'Demo Operator', role: 'owner', is_active: true, last_seen_at: ago(0) },
    { id: 'demo-mgr', email: 'manager@example.co.za', full_name: 'Shop Manager', role: 'manager', is_active: true, last_seen_at: ago(19) },
    { id: 'demo-staff-1', email: 'saturday@example.co.za', full_name: 'Saturday Assistant', role: 'staff', is_active: true, last_seen_at: ago(62) },
    { id: 'demo-staff-2', email: 'former@example.co.za', full_name: 'Former Assistant', role: 'staff', is_active: false, last_seen_at: ago(1400) },
  ]
}

export function demoInvitations() {
  const d = new Date(TODAY)
  d.setDate(d.getDate() - 2)
  const expires = new Date(TODAY)
  expires.setDate(expires.getDate() + 12)
  return [
    {
      id: 'demo-inv-1',
      email: 'newstaff@example.co.za',
      role: 'staff',
      invited_by_email: 'demo@coquis.co.za',
      note: 'Weekday shop floor',
      created_at: d.toISOString(),
      expires_at: expires.toISOString(),
      accepted_at: null,
    },
  ]
}

/** Example customer accounts, so the Customers page reads sensibly in demo mode. */
export function demoCustomers() {
  return [
    { id: 'demo-c1', email: 'johan.vandermerwe@example.co.za', first_name: 'Johan', last_name: 'van der Merwe', phone: '082 123 4567', city: 'Centurion', province: 'Gauteng', marketing_opt_in: true, is_blocked: false, admin_notes: null, created_at: new Date(TODAY.getTime() - 86400000 * 120).toISOString(), order_count: 7, total_spent: 8420, last_order_at: new Date(TODAY.getTime() - 86400000 * 3).toISOString() },
    { id: 'demo-c2', email: 'thabo.nkosi@example.co.za', first_name: 'Thabo', last_name: 'Nkosi', phone: '083 555 1212', city: 'Pretoria', province: 'Gauteng', marketing_opt_in: true, is_blocked: false, admin_notes: 'Prefers collection over delivery', created_at: new Date(TODAY.getTime() - 86400000 * 80).toISOString(), order_count: 4, total_spent: 5195, last_order_at: new Date(TODAY.getTime() - 86400000 * 11).toISOString() },
    { id: 'demo-c3', email: 'pieter.botha@example.co.za', first_name: 'Pieter', last_name: 'Botha', phone: null, city: 'Midrand', province: 'Gauteng', marketing_opt_in: false, is_blocked: false, admin_notes: null, created_at: new Date(TODAY.getTime() - 86400000 * 45).toISOString(), order_count: 2, total_spent: 1780, last_order_at: new Date(TODAY.getTime() - 86400000 * 20).toISOString() },
    { id: 'demo-c4', email: 'sipho.dlamini@example.co.za', first_name: 'Sipho', last_name: 'Dlamini', phone: '071 900 4455', city: 'Johannesburg', province: 'Gauteng', marketing_opt_in: false, is_blocked: true, admin_notes: 'Three chargebacks — do not ship until paid by EFT', created_at: new Date(TODAY.getTime() - 86400000 * 200).toISOString(), order_count: 1, total_spent: 640, last_order_at: new Date(TODAY.getTime() - 86400000 * 150).toISOString() },
    { id: 'demo-c5', email: 'kagiso.mokoena@example.co.za', first_name: 'Kagiso', last_name: 'Mokoena', phone: '084 221 8899', city: 'Polokwane', province: 'Limpopo', marketing_opt_in: true, is_blocked: false, admin_notes: null, created_at: new Date(TODAY.getTime() - 86400000 * 9).toISOString(), order_count: 0, total_spent: 0, last_order_at: null },
  ]
}

// ===========================================================================
//  Suppliers and invoicing
// ===========================================================================
//  Real South African menswear brands, with deliberately invented contact
//  details — an example- domain and a 555 number cannot be mistaken for real
//  ones and cannot be dialled by accident. VAT numbers follow the SARS shape
//  (ten digits starting with 4) so the layout is exercised properly, but they
//  are not valid registrations.
// ===========================================================================

const DEMO_PREFIX = { invoice: 'INV', quote: 'QUO', proforma: 'PRO', credit_note: 'CRN' }

const SUPPLIERS = [
  {
    name: 'Lorenzini Shirts',
    trading_as: 'Lorenzini SA (Pty) Ltd',
    contact_name: 'Marco Bianchi',
    email: 'accounts@example-lorenzini.co.za',
    phone: '011 555 0180',
    vat_number: '4120345678',
    registration_number: '2011/045122/07',
    address_line1: 'Unit 14, Kyalami Business Park',
    address_line2: 'Main Reef Road',
    city: 'Midrand',
    province: 'Gauteng',
    postal_code: '1685',
    account_number: 'COQ-0041',
    payment_terms_days: 30,
  },
  {
    name: 'Carducci Menswear',
    contact_name: 'Nadia Petersen',
    email: 'orders@example-carducci.co.za',
    phone: '021 555 0144',
    vat_number: '4230998877',
    registration_number: '2004/113987/07',
    address_line1: '22 Papenboom Road',
    city: 'Cape Town',
    province: 'Western Cape',
    postal_code: '7700',
    account_number: 'CQ-118',
    payment_terms_days: 45,
  },
  {
    name: 'Jonathan D Distribution',
    contact_name: 'Rajesh Naidoo',
    email: 'sales@example-jonathand.co.za',
    phone: '031 555 0199',
    vat_number: '4455221133',
    address_line1: '8 Umgeni Business Park',
    city: 'Durban',
    province: 'KwaZulu-Natal',
    postal_code: '4001',
    payment_terms_days: 30,
  },
  {
    name: 'Galway Centre Property Trust',
    contact_name: 'Elsabe Kruger',
    email: 'leasing@example-galway.co.za',
    phone: '012 555 0102',
    vat_number: '4901234567',
    address_line1: '295 Galway Avenue',
    city: 'Centurion',
    province: 'Gauteng',
    postal_code: '0157',
    payment_terms_days: 7,
    notes: 'Monthly rent and utilities. Invoice arrives on the 25th.',
  },
  {
    name: 'Pretoria Dry Cleaners',
    contact_name: 'Willem Steyn',
    email: 'admin@example-ptadrycleaners.co.za',
    phone: '012 555 0166',
    address_line1: '17 Rabie Street',
    city: 'Pretoria',
    province: 'Gauteng',
    postal_code: '0181',
    payment_terms_days: 14,
    notes: 'Not VAT registered.',
  },
].map((supplier, index) => ({
  id: `demo-sup-${index + 1}`,
  trading_as: null,
  website: null,
  registration_number: null,
  address_line2: null,
  country: 'South Africa',
  account_number: null,
  notes: null,
  is_active: true,
  created_at: new Date(TODAY.getTime() - 86400000 * (300 - index * 30)).toISOString(),
  updated_at: new Date(TODAY.getTime() - 86400000 * 4).toISOString(),
  ...supplier,
}))

export function demoSuppliers() {
  return SUPPLIERS
}

/**
 * The same formula as computeTotals() in lib/invoice.js, inlined so this module
 * keeps its one-way dependency on nothing but the catalogue.
 */
function demoTotals(items) {
  const subtotal =
    Math.round(
      items.reduce((sum, item) => sum + Math.round(item.quantity * item.unit_price * 100) / 100, 0) *
        100,
    ) / 100
  return {
    subtotal,
    taxAmount: Math.round(((subtotal * 15) / 115) * 100) / 100,
    total: subtotal,
  }
}

/**
 * A handful of documents covering every state the list page can show: a draft,
 * a sent invoice inside its terms, one that has gone past its due date, a paid
 * one, a quote to a corporate customer, and a credit note.
 */
function buildInvoices() {
  const at = (daysAgo) => new Date(TODAY.getTime() - 86400000 * daysAgo)
  const iso = (date) => date.toISOString().slice(0, 10)
  const shirts = catalogue.filter((p) => /shirt/i.test(p.category ?? '')).slice(0, 8)
  const pickShirt = (i) => shirts[i % shirts.length] ?? catalogue[i % catalogue.length]

  const fromProduct = (i, quantity) => {
    const product = pickShirt(i)
    return {
      product_id: `demo-${product.id}`,
      sku: product.sku ?? null,
      description: product.name,
      detail: null,
      quantity,
      unit: null,
      // Trade price, not the shelf price: this is what the shop pays for it.
      unit_price: Math.round(product.price * 0.55 * 100) / 100,
    }
  }

  const free = (description, detail, quantity, unitPrice, unit = null) => ({
    product_id: null,
    sku: null,
    description,
    detail,
    quantity,
    unit,
    unit_price: unitPrice,
  })

  const specs = [
    {
      kind: 'invoice', status: 'sent', supplier: 0, issued: 12, dueIn: 18, reference: 'PO-4471',
      items: [fromProduct(0, 12), fromProduct(1, 6), fromProduct(2, 6)],
    },
    {
      kind: 'invoice', status: 'sent', supplier: 3, issued: 47, dueIn: -12, reference: 'Rent — August',
      items: [
        free('Shop rental — Galway Centre', 'Unit 4, 128 m²', 1, 18500, 'month'),
        free('Utilities recovery', null, 1, 2340.75),
      ],
    },
    {
      kind: 'invoice', status: 'paid', supplier: 1, issued: 62, dueIn: 32, reference: 'PO-4408',
      items: [fromProduct(3, 24), fromProduct(4, 12)],
    },
    {
      kind: 'quote', status: 'sent', supplier: null, issued: 4, dueIn: 26,
      billTo: 'Vaughn & Associates Attorneys',
      items: [
        free('Corporate shirt programme — 18 staff', 'Embroidered left chest, two shirts each', 36, 649),
        free('Embroidery setup', 'One-off digitising of the logo', 1, 850),
      ],
    },
    {
      kind: 'invoice', status: 'draft', supplier: 2, issued: 1, dueIn: 31,
      items: [fromProduct(5, 18)],
    },
    {
      kind: 'credit_note', status: 'sent', supplier: 0, issued: 21, dueIn: null,
      reference: 'Against INV-2026-0001',
      // Positive amounts, as a credit note is written in practice: the heading
      // already says it is a credit, and negating the lines as well would give
      // a document that credits nothing.
      items: [free('Returned — colour fault on three shirts', 'Agreed with Marco on the 14th', 3, 899)],
    },
  ]

  return specs.map((spec, index) => {
    const supplier = spec.supplier === null ? null : SUPPLIERS[spec.supplier]
    const issued = at(spec.issued)
    const totals = demoTotals(spec.items)
    const id = `demo-inv-${index + 1}`
    return {
      id,
      number: `${DEMO_PREFIX[spec.kind]}-2026-${String(index + 1).padStart(4, '0')}`,
      kind: spec.kind,
      status: spec.status,
      supplier_id: supplier?.id ?? null,
      supplier_name: supplier?.name ?? null,
      customer_id: null,
      order_id: null,
      bill_to_name: supplier?.name ?? spec.billTo,
      bill_to_contact: supplier?.contact_name ?? 'Andrea Vaughn',
      bill_to_email: supplier?.email ?? 'accounts@example-vaughn.co.za',
      bill_to_phone: supplier?.phone ?? '012 555 0123',
      bill_to_vat_number: supplier?.vat_number ?? '4770112233',
      bill_to_reg_number: supplier?.registration_number ?? null,
      bill_to_address:
        [supplier?.address_line1, supplier?.address_line2].filter(Boolean).join('\n') ||
        '4 Jean Avenue\nDie Hoewes',
      bill_to_city: supplier?.city ?? 'Centurion',
      bill_to_province: supplier?.province ?? 'Gauteng',
      bill_to_postal_code: supplier?.postal_code ?? '0157',
      bill_to_country: 'South Africa',
      issue_date: iso(issued),
      due_date: spec.dueIn === null ? null : iso(at(spec.issued - spec.dueIn)),
      reference: spec.reference ?? null,
      notes: null,
      terms: 'Payment is due within 30 days of the invoice date.',
      internal_notes: null,
      currency: 'ZAR',
      prices_include_tax: true,
      tax_rate: 15,
      discount_type: 'none',
      discount_value: 0,
      shipping_amount: 0,
      subtotal: totals.subtotal,
      discount_amount: 0,
      tax_amount: totals.taxAmount,
      total: totals.total,
      item_count: spec.items.length,
      amount_paid: spec.status === 'paid' ? totals.total : 0,
      balance_due: spec.status === 'paid' ? 0 : totals.total,
      is_overdue: spec.status === 'sent' && spec.kind === 'invoice' && spec.dueIn !== null && spec.dueIn < 0,
      days_to_due: spec.dueIn,
      paid_at: spec.status === 'paid' ? at(spec.issued - 20).toISOString() : null,
      sent_at: spec.status === 'draft' ? null : issued.toISOString(),
      created_by: null,
      created_at: issued.toISOString(),
      updated_at: issued.toISOString(),
      items: spec.items.map((item, i) => ({
        ...item,
        id: `${id}-line-${i}`,
        invoice_id: id,
        sort_order: i,
        line_total: Math.round(item.quantity * item.unit_price * 100) / 100,
      })),
    }
  })
}

const INVOICES = buildInvoices()

export function demoInvoices({ kind, status, search } = {}) {
  const query = (search ?? '').trim().toLowerCase()
  return INVOICES.filter((invoice) => {
    if (kind && kind !== 'all' && invoice.kind !== kind) return false
    if (status && status !== 'all' && invoice.status !== status) return false
    if (!query) return true
    return (
      invoice.number.toLowerCase().includes(query) ||
      invoice.bill_to_name.toLowerCase().includes(query) ||
      (invoice.reference ?? '').toLowerCase().includes(query)
    )
  })
}

export function demoInvoice(id) {
  return INVOICES.find((invoice) => invoice.id === id) ?? null
}
